import React, { useCallback, useEffect, useState, useContext } from 'react';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { wizardProps, WizardContext } from 'wmic-pe-portals-custom-wizard-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDocumentTitle, useWizardModals, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { TranslatorContext } from '@jutro/locale';
import { WMICUnderwritingIssues } from 'wmic-pe-components-platform-react';
import { DocumentService } from 'wmic-pe-capability-gateway-policycommon';
import { WMICDocuments } from 'wmic-pe-capability-gateway-common-react';
import { WMICErrorHandler, WMICQuoteUtil } from 'wmic-pe-capability-quoteandbind-common-react';
import { CONSTANTS, WMICLogger, WMICVariousUtil, parseErrorMessage, FlowStepId } from 'wmic-pe-portals-utils-js';

import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import messages from './WMICCPQuote.messages';
import metadata from './WMICCPQuote.metadata.json5';

function WMICCPQuote(props) {
    const { wizardData: submissionVM, updateWizardData, markStepSubmitted, stopErrorNavigation } = props;
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const { isSkipping } = useContext(WizardContext);
    const { isComponentValid, onValidate, initialValidation, registerComponentValidation, registerInitialComponentValidation } = useValidation('QuotePage');
    const history = useHistory();
    const translator = useContext(TranslatorContext);
    const [userDocuments, setUserDocuments] = useState([]);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { setWizardLoading } = useWizardModals();

    useDocumentTitle(translator(messages.wizard), submissionVM);

    const validateSubmission = useCallback(async () => {
        const newSubmissionVM = viewModelService.clone(submissionVM);
        _.unset(newSubmissionVM.value, 'bindData');
        const result = await LoadSaveService.validateSubmissionForIssue(newSubmissionVM.value, authHeader);
        _.extend(submissionVM.value, result);
        updateWizardData(submissionVM);
        markStepSubmitted(); // the step needs to be marked as submitted for the errors associated with this step to show up
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [LoadSaveService, authHeader, submissionVM, viewModelService, setWizardLoading, markStepSubmitted]);

    const fetchDocuments = useCallback(async () => {
        const documents = await DocumentService.getDocumentsForJobNumber(submissionVM.quoteID.value, authHeader);
        if (userDocuments.length !== documents.length) {
            setUserDocuments(documents);
        }
    }, [authHeader, submissionVM, userDocuments]);

    const underwritingIssues = _.get(submissionVM, 'errorsAndWarnings.underwritingIssues.value', []);

    const hasQuoteBlockingUWIssues = useCallback(
        () => WMICQuoteUtil.hasQuoteBlockingUWIssues(submissionVM),
        [submissionVM]
    );

    const isTransactionQuoted = useCallback(
        () => submissionVM?.baseData?.periodStatus?.value?.code === CONSTANTS.QUOTED,
        [submissionVM]
    );

    const checkComponentValidation = useCallback(
        () =>
            !(
                WMICQuoteUtil.isBlockingUWIssuePresent(submissionVM) ||
                WMICQuoteUtil.hasValidationErrors(submissionVM) ||
                hasQuoteBlockingUWIssues() ||
                !isTransactionQuoted()
            ),
        [submissionVM, hasQuoteBlockingUWIssues, isTransactionQuoted]
    );

    const checkInitialValidation = useCallback(async () => {
        if (isSkipping) {
            let isValidateSubmissionSuccessful = false;
            await validateSubmission()
                .then(() => { isValidateSubmissionSuccessful = true; })
                .catch(() => { isValidateSubmissionSuccessful = false; });
            return isValidateSubmissionSuccessful && checkComponentValidation();
        }
        return true;
    }, [isSkipping, validateSubmission, checkComponentValidation]);

    useEffect(() => {
        registerInitialComponentValidation(checkInitialValidation);
        registerComponentValidation(checkComponentValidation);
        stopErrorNavigation();
    }, [
        registerInitialComponentValidation,
        checkInitialValidation,
        registerComponentValidation,
        checkComponentValidation,
        stopErrorNavigation,
    ]);

    const loadAdditionalInformation = useCallback(async () => {
        const isBMSi = _.get(submissionVM, 'isFromBMS_WMIC.value');
        const defaultedPropertiesToReviewExist = _.get(submissionVM, 'defaultedProperties_WMIC.value', [])
            .some(p => p.propertyDisplayName && p.status !== CONSTANTS.REVIEWED);
        const shouldRunValidateSubmission = !isSkipping && (!isBMSi || (isBMSi && !defaultedPropertiesToReviewExist));

        setWizardLoading(!isSkipping, translator(commonMessages.loading));
        await Promise.all([
            shouldRunValidateSubmission ? validateSubmission() : null,
            fetchDocuments()
        ]).catch(error => {
            const jobID = submissionVM.quoteID.value;
            WMICLogger.error('Unable to load additional information', error);
            WMICErrorHandler.processAsModal(parseErrorMessage(error))
                .catch(() => history.push(`/quotes/${jobID}/summary`));
        }).finally(() => setWizardLoading(false));
    }, [isSkipping, translator, submissionVM, validateSubmission, fetchDocuments, setWizardLoading, history]);

    useEffect(() => {
        loadAdditionalInformation();
    }, [loadAdditionalInformation]);

    const onNext = useCallback(async () => {
        if (!isComponentValid) {
            WMICVariousUtil.scrollToTop();
            return false;
        }
        return submissionVM;
    }, [submissionVM, isComponentValid]);

    const isQuoteInfoAvailable = !hasQuoteBlockingUWIssues() && isTransactionQuoted();
    const isUploadDocumentsSectionVisible = !hasQuoteBlockingUWIssues() && isTransactionQuoted();

    const overrideProps = {
        wmicFlexAlertMessage: {
            jobVM: submissionVM,
        },
        wmicQuoteAlertMessage: {
            jobVM: submissionVM,
        },
        quotePagePremiumCosts: {
            premium: _.get(submissionVM, 'quoteData.offeredQuotes.value[0].premium', {}),
            jobVM: submissionVM,
            updateJobVM: updateWizardData,
            enableFlex: false,
            enableBrokerFees: true,
            visible: isQuoteInfoAvailable,
        },
        quotePageUWIssues: {
            underwritingIssues,
            quoteID: _.get(submissionVM, 'quoteID.value'),
            authHeader,
            jobVM: submissionVM,
        },
        quotePageSummary: {
            jobVM: submissionVM,
            visible: isQuoteInfoAvailable,
        },
        quotePageUploadDocuments: {
            documents: userDocuments,
            updateDocuments: setUserDocuments,
            value: {
                documentDTOs: userDocuments,
                canUploadDocument: true,
                jobNumber: submissionVM.quoteID.value,
            },
            visible: isUploadDocumentsSectionVisible,
        },
    };

    const resolvers = {
        resolveComponentMap: {
            WMICUnderwritingIssues,
            WMICDocuments,
        },
    };

    return (
        <WMICWizardSubmissionPage
            onNext={onNext}
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            cancelLabel={translator(commonMessages.saveAndExit)}
            flowStepId={FlowStepId.QUOTE}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICCPQuote.propTypes = wizardProps;

export default WMICCPQuote;
